import React, { FC, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { RoundingSettingField } from '../../../../components/rounding-setting-field';
import { useSelected } from '../../../../hooks/company/index';
import { useFormat, useLocale, ValuationHooks } from '../../../../hooks/index';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { toNumber } from '../../../../utils/to-number.util';
import { ConversionSummary } from '../conversion-summary/index';
import { ChildrenFormProps } from '../convert-modal.types';
import { FormSchema, stepOne as stepOneFormSchema } from '../convert-modal.validation';
import { EventCombobox } from '../event-combobox';
import { SafeSelect } from '../safe-select/index';
import { ShareClassCombobox } from '../share-class-combobox';

export type StepOneProps = ChildrenFormProps;

export const StepOne: FC<StepOneProps> = ({
  handleCloseModal,
  nextFormStep,
  control,
  setFormData,
  companyId,
  filedState,
}) => {
  const { messagesLocale } = useLocale();
  const [isOpenCalendar, setOpenCalendar] = useState(false);
  const [isConversionSummaryLoading, setConversionSummaryLoading] = useState(false);
  const [isConversionSummaryRefused, setConversionSummaryRefused] = useState(false);
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();
  const { success } = stepOneFormSchema.safeParse(stepOne);
  const isNoValuation = stepOne?.linkedEvent?.id === 'no-valuation';

  const { valuation } = ValuationHooks.useValuation({
    id: isNoValuation ? '' : stepOne?.linkedEvent?.id || '',
  });

  const isValid = success && !filedState('stepOne').invalid;

  const { selectedCompany } = useSelected();

  const isValidSafes = isNoValuation
    ? stepOne?.safes?.every((safe) => safe?.floorValue && safe?.floorValue > 0)
    : true;

  const issuedShares = valuation?.issuedShares || valuation?.issuedSharesOnStart;

  useEffect(() => {
    if (isValid) return;
    setConversionSummaryLoading(false);
    setConversionSummaryRefused(false);
  }, [isValid]);

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <FormDatePicker
          inputValue={stepOne?.conversionDate ? format(stepOne?.conversionDate, 'dd/MM/yyyy') : ''}
          inputWrapperClassName="w-full"
          isDefaultOpenCalendar={isOpenCalendar}
          onSelect={(date) => {
            setFormData('stepOne.conversionDate', date);
            setOpenCalendar(false);
          }}
          placeholder={<AppFormattedMessage id="CONVERSION_DATE" />}
          value={stepOne?.conversionDate}
        />
        <EventCombobox companyId={companyId} control={control} />
        {!!(stepOne?.linkedEvent?.sharePrice && stepOne?.linkedEvent?.sharePrice !== 0) && (
          <div className="flex w-full items-center justify-between rounded-md px-4 py-3 shadow-sm">
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SHARES_PRICE" />
            </span>
            <span className="rounded bg-brand-25 px-3 py-1 text-sm font-[550] text-brand-700">
              {selectedCompany?.currency?.symbol} {toRound(stepOne?.linkedEvent?.sharePrice, 2)}
            </span>
          </div>
        )}
        <ShareClassCombobox companyId={companyId} control={control} />
        <RoundingSettingField control={control} name="stepOne.roundingSetting" />
        <SafeSelect
          companyId={companyId}
          control={control}
          currencySign={selectedCompany?.currency?.symbol}
          setValue={setFormData}
        />
        {isValid && (
          <ConversionSummary
            allSafeIds={stepOne?.safeIds}
            companyId={companyId}
            conversionSummary={stepOne?.conversionSummary}
            currencySign={selectedCompany?.currency?.symbol}
            onErrorChange={setConversionSummaryRefused}
            onLoadingChange={setConversionSummaryLoading}
            postMoneyValuation={toNumber(issuedShares) * toNumber(valuation?.sharePrice)}
            preMoneyValuation={
              toNumber(valuation?.issuedSharesOnStart) * toNumber(valuation?.sharePrice)
            }
            roundingSetting={stepOne?.roundingSetting}
            setValue={setFormData}
            valuationId={isNoValuation ? undefined : stepOne?.linkedEvent?.id}
            valuationIssuedShares={isNoValuation ? undefined : issuedShares}
          />
        )}
        <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
          <DropDown
            answer={messagesLocale['LINKED_EVENT_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_IS_LINKED_EVENT" />}
          />
          <DropDown
            answer={messagesLocale['EVENT_SHARE_PRICE_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_IS_EVENT_SHARE_PRICE" />}
          />
          <DropDown
            answer={messagesLocale['PERCENT_OWNED_AT_CONVERSION_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_IS_PERCENT_OWNED_AT_CONVERSION" />}
          />
          <DropDown
            answer={messagesLocale['METHOD_OF_CONVERSION_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_IS_METHOD_OF_CONVERSION" />}
          />
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={
              !isValid || !isValidSafes || isConversionSummaryLoading || isConversionSummaryRefused
            }
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="NEXT" />
          </Button>
        </div>
      </div>
    </>
  );
};
